"use client";

import BulkImportArticle from "../../../components/BulkImportArticle";

const data = {
  title: "Bulk Lead Import",
  audience: "For: Anyone adding many leads at once from a spreadsheet",
  intro: [
    "This tool lets you upload many leads at once from an Excel/CSV-style sheet, instead of adding them one by one. It explains what each row needs and what will cause a row to be rejected — no technical background required.",
  ],
  createsOnlyNote:
    "This tool only creates new leads — it never updates an existing one. If the same lead is uploaded again, it is rejected as a duplicate rather than refreshed.",
  steps: {
    commitVerb: "Create the leads",
    commitLabel: "Create",
    allOrNothing:
      "Creating is all-or-nothing at the Check stage: if even one row fails the checks, none of the leads are created — the whole batch is rejected. This is why checking first is strongly recommended.",
  },
  requiredFields: [
    { field: "Full Name", needs: "The lead's contact person name. Required on every row." },
    { field: "Phone", needs: "Required on every row." },
    { field: "Move-In Date", needs: "A valid date. Required on every row." },
  ],
  conditionalFields: [
    { field: "Priority", rule: "If filled in, must be exactly Hot, Warm, or Cold." },
    { field: "Potential", rule: "Optional estimated deal value — a number." },
    { field: "Email", rule: "Optional." },
    { field: "Company Name", rule: "Optional on its own, but needed for the duplicate check below." },
    { field: "Move-Out Date / City / Address / Notes", rule: "Optional." },
  ],
  matchFields: [
    {
      field: "Lead Source",
      rule: "Must exactly match an existing Lead Source. Checked at both steps — a mismatch blocks creation.",
    },
    {
      field: "Industry",
      rule: "Must exactly match an existing Industry. Checked at both steps — a mismatch blocks creation.",
    },
    {
      field: "Lead Status",
      rule: "Must exactly match an existing Lead Status. Checked at both steps — a mismatch blocks creation.",
    },
    {
      field: "Campaign Name",
      rule: "Must match an existing Campaign — but this is only verified at the Check step. At Create, a name that doesn't match does NOT block the lead; the lead is created without a campaign link. Double-check spelling before uploading.",
    },
  ],
  matchNote:
    "If you fill these in, the value must exactly match a record already set up for your company. The match is case-sensitive — 'Cold Call' and 'COLD CALL' are treated as different, and a differently-capitalized name is rejected as 'not found'.",
  duplicateRules: [
    {
      condition: "The same Company Name + Phone Number appears more than once in your sheet",
      result: "Rejected — you can't list the same lead twice in one upload.",
    },
    {
      condition: "That Company Name + Phone Number already exists as a lead in the system",
      result: "Rejected — it's already on record.",
    },
    {
      condition: "Either Company Name or Phone is blank on the row",
      result: "The duplicate check is skipped — it only runs when both are present.",
    },
  ],
  mistakes: [
    { problem: "Full Name / Phone / Move-In Date is missing", meaning: "Every row needs all three." },
    { problem: "Lead Source, Industry, or Lead Status doesn't match", meaning: "Check spelling and capitalization — it must match exactly, and this blocks creation." },
    { problem: "Campaign Name doesn't match", meaning: "Flagged at Check. At Create it won't block, but the lead will have no campaign link." },
    { problem: "Same Company Name + Phone appears twice in the sheet", meaning: "List each lead only once per upload." },
    { problem: "Company Name + Phone already exists as a lead", meaning: "That lead is already on record — check before re-importing." },
  ],
  onApply: [
    "Each lead gets a sequential ID (LEAD00001, LEAD00002, …), continuing from the last one used.",
    "Lead Source, Industry, and Lead Status are linked to their matching records; Campaign is linked only if it matched (otherwise the lead is created unlinked).",
    "You — the person doing the import — are set as both the creator and the assigned owner of every lead. Any 'assigned to' column in the sheet is ignored.",
    "A note is added to each lead's history showing its starting status.",
    "Leads keep the same order as your sheet when viewed in lists sorted by creation time.",
  ],
  workflow: [
    "Build your sheet with all the leads you want to add.",
    "Run the Check step first and review the list of problem rows.",
    "Fix each issue in the sheet using the tables above as a guide.",
    "Run the Check step again until every row passes.",
    "Only then run Create to actually add the leads.",
    "Tip: double-check Campaign Name spelling even after a clean Check — it's the one field where Check and Create don't behave the same way.",
  ],
};

export default function BulkLeadImportPage() {
  return <BulkImportArticle data={data} />;
}
